import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, MagnifyingGlassIcon, SparklesIcon } from '@heroicons/react/24/solid';
import styles from '../PlansConversionLandingPage.module.css';
import DemoAidaResponse from './DemoAidaResponse';
import { DEMO_DISEASES, DEMO_PAID_PLAN } from './demoComparisonData';
import { formatPctIncrease, useDemoDiseaseStats } from './useDemoDiseaseStats';
import type { DemoDiseaseId, DemoDiseaseStats } from './demoComparisonTypes';

type DemoComparisonSectionProps = {
  reduceMotion: boolean;
  fadeUp: (reduceMotion: boolean) => Record<string, unknown>;
};

function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

function StatRows({
  stats,
  baseline,
  variant,
}: {
  stats: DemoDiseaseStats;
  baseline?: DemoDiseaseStats;
  variant: 'academic' | 'paid';
}) {
  const rows: { label: string; value: number; baselineValue?: number }[] = [
    { label: 'Associated genes', value: stats.genes, baselineValue: baseline?.genes },
    { label: 'Variants indexed', value: stats.variants, baselineValue: baseline?.variants },
    { label: 'Studies included', value: stats.studies, baselineValue: baseline?.studies },
  ];

  return (
    <dl className={styles.demoStats}>
      {rows.map(row => {
        const pct =
          variant === 'paid' && row.baselineValue != null
            ? formatPctIncrease(row.baselineValue, row.value)
            : null;

        return (
          <div key={row.label} className={styles.demoStatRow}>
            <dt>{row.label}</dt>
            <dd>
              <span className={styles.demoStatValue}>{formatCount(row.value)}</span>
              {pct ? <span className={styles.demoStatDelta}>{pct}</span> : null}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export default function DemoComparisonSection({ reduceMotion, fadeUp }: DemoComparisonSectionProps) {
  const { getStats } = useDemoDiseaseStats();
  const [selectedId, setSelectedId] = useState<DemoDiseaseId>('ehlers-danlos');
  const [aidaOpen, setAidaOpen] = useState(false);

  const disease = DEMO_DISEASES.find(d => d.id === selectedId)!;
  const { academic, full } = getStats(selectedId);

  const handleDiseaseChange = (id: DemoDiseaseId) => {
    setSelectedId(id);
    setAidaOpen(false);
  };

  return (
    <section id="demo-comparison" className={styles.demoSection} aria-labelledby="demo-comparison-title">
      <div className={styles.inner}>
        <motion.div {...fadeUp(!!reduceMotion)} className={styles.textCenter}>
          <p className={styles.demoKicker}>Try it yourself</p>
          <h2 id="demo-comparison-title" className={styles.demoTitle}>
            See what you&apos;re missing — <span className={styles.demoTitleAccent}>in real numbers</span>
          </h2>
          <p className={styles.demoLead}>
            Search a disease and compare what the Academic plan returns vs. what Standard + AIDA unlocks.
          </p>
        </motion.div>

        <motion.div {...fadeUp(!!reduceMotion)} className={styles.demoPanel}>
          <div className={styles.demoPanelHeader}>
            <p className={styles.demoSearching}>
              Searching for: <strong>{disease.label}</strong>
            </p>
            <p className={styles.demoPanelHint}>Here&apos;s what you&apos;d discover with each plan</p>
          </div>

          <div className={styles.demoControls}>
            <div className={styles.demoSearchField} role="search">
              <MagnifyingGlassIcon className={styles.demoSearchIcon} aria-hidden />
              <input
                type="text"
                className={styles.demoSearchInput}
                value={disease.label}
                readOnly
                aria-readonly="true"
                aria-label="Selected disease"
                onKeyDown={e => e.preventDefault()}
                onChange={() => undefined}
              />
            </div>
            <div className={styles.demoPills} role="tablist" aria-label="Example diseases">
              {DEMO_DISEASES.map(d => (
                <button
                  key={d.id}
                  type="button"
                  role="tab"
                  aria-selected={d.id === selectedId}
                  className={d.id === selectedId ? styles.demoPillActive : styles.demoPill}
                  onClick={() => handleDiseaseChange(d.id)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.demoCards}>
            <article className={styles.demoCardAcademic}>
              <header className={styles.demoCardHead}>
                <h3>Academic (Free)</h3>
                <p>Your current plan</p>
              </header>
              <p className={styles.demoDiseaseName}>{disease.label}</p>
              <StatRows stats={academic} variant="academic" />
            </article>

            <article className={styles.demoCardPaid}>
              <header className={styles.demoCardHead}>
                <h3>{DEMO_PAID_PLAN.title}</h3>
                <p>{DEMO_PAID_PLAN.subtitle}</p>
              </header>
              <p className={styles.demoDiseaseName}>{disease.label}</p>
              <StatRows stats={full} baseline={academic} variant="paid" />
              <button
                type="button"
                className={styles.demoAidaBtn}
                aria-expanded={aidaOpen}
                onClick={() => setAidaOpen(open => !open)}
              >
                <SparklesIcon className={styles.iconSm} aria-hidden />
                <span>See what AIDA answered</span>
                <ArrowRightIcon className={styles.iconSm} aria-hidden />
              </button>
            </article>
          </div>

          {aidaOpen ? (
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 12 }}
              animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className={styles.demoAidaCard}
              role="region"
              aria-label="AIDA response preview"
            >
              <div className={styles.demoAidaCardHead}>
                <img
                  src="/static/images/eyesAida.svg"
                  alt="AIDA"
                  className={styles.demoAidaLogo}
                  width={32}
                  height={32}
                />
                <span>AIDA</span>
                <span className={styles.demoAidaBadge}>Preview</span>
              </div>
              <DemoAidaResponse content={disease.aida} contentKey={selectedId} />
            </motion.div>
          ) : null}
        </motion.div>
      </div>
    </section>
  );
}
